import type { ParsedTranscriptEntry } from './export-types';
import type { Transcript, TranscriptSegment } from '@consigli/types';

export const msToTimeString = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/** Oldest transcript first — a meeting can produce several. */
export const sortTranscriptsByCreation = (transcripts: Transcript[]): Transcript[] =>
  [...transcripts].sort((a, b) => {
    const tsA = a.createdDateTime || a.creationTimestamp || a.createdAt;
    const tsB = b.createdDateTime || b.creationTimestamp || b.createdAt;
    return new Date(tsA || '').getTime() - new Date(tsB || '').getTime();
  });

export const parseTranscriptSegments = (transcripts: Transcript[]): ParsedTranscriptEntry[] => {
  const parsed: ParsedTranscriptEntry[] = [];

  transcripts.forEach((transcript, index) => {
    const segmentOffset = index * 3600;
    const segments = transcript.segments as TranscriptSegment[] | undefined;
    segments?.forEach((seg) => {
      const rawLabel = (seg.speakerName as string | undefined) ?? null;
      parsed.push({
        speaker:
          (seg.assignedUserName as string | undefined) || rawLabel || seg.speaker || 'Unknown',
        text:
          (seg.cleanedTranscriptionText as string | undefined) ||
          seg.enrichedTranscriptionText ||
          seg.transcriptionText ||
          '',
        startTime: msToTimeString(seg.startMs ?? 0),
        endTime: msToTimeString(seg.endMs ?? 0),
        timestamp: (seg.startMs ?? 0) / 1000 + segmentOffset,
        segmentId: seg.id as string | undefined,
        speakerName: rawLabel,
        assignedMeetingUserId: (seg.assignedMeetingUserId as string | undefined) ?? null,
      });
    });
  });

  parsed.sort((a, b) => a.timestamp - b.timestamp);
  return parsed;
};

export const groupBySpeaker = (entries: ParsedTranscriptEntry[]): ParsedTranscriptEntry[] => {
  if (entries.length === 0) return [];

  const grouped: ParsedTranscriptEntry[] = [];
  let current = { ...entries[0] };

  for (let i = 1; i < entries.length; i++) {
    const next = entries[i];
    if (next.speaker === current.speaker && next.timestamp - current.timestamp < 30) {
      current.text += ' ' + next.text;
      current.endTime = next.endTime;
    } else {
      grouped.push(current);
      current = { ...next };
    }
  }

  grouped.push(current);
  return grouped;
};
